import React from 'react'

import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
	useTheme,
	Chip,
	Checkbox,
	ListItemText,
	Icon
} from '@material-ui/core'

import { AddOutlined } from '@material-ui/icons'
import { cloneDeep, groupBy, isEqual } from 'lodash'

import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { v4 as uuid } from 'uuid'
import moment from 'moment'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { isMedTimeBlockApplicable } from '../../../utils'

const useStyles = makeStyles((theme) => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	headerButton: {
		fontFamily: 'Nunito',
		fontWeight: '700',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	addMedButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		},
		marginBottom: 20
	},
	checkbox: {
		'&.Mui-disabled.Mui-checked .MuiIconButton-label .MuiSvgIcon-root': {
			fill: '#A2A1A4'
		}
	},
	avatar: {
		background: '#C4C4C4',
		height: '32px',
		width: '32px',
		marginTop: '12px',
		marginBottom: '12px'
	},
	editButton: {
		color: '#6B4AD6',
		fontWeight: '600px',
		fontSize: '16px',
		textTransform: 'capitalize'
	},
	editLabel: {
		fontWeight: '600',
		textTransform: 'none',
		fontSize: '16px'
	},
	fieldWrapper: {
		marginBottom: 20
	},
	field: {
		'&.MuiTextField-root': {
			minWidth: '100%'
		},
		'& .MuiFilledInput-underline': {
			minWidth: '100%'
		}
	},
	autoComplete: {
		'& .MuiFilledInput-root': {
			paddingTop: '17px'
		},
		'& .MuiInputLabel-filled': {
			transform: 'translate(12px, 22px) scale(1)'
		},
		'& .Mui-focused': {
			'& .MuiAutocomplete-endAdornment svg': {
				fill: theme.palette.secondary.main
			}
		},
		'& .MuiAutocomplete-endAdornment svg': {
			fill: '#777679'
		},
		"& .MuiAutocomplete-inputRoot[class*='MuiFilledInput-root'][class*='MuiFilledInput-marginDense'] .MuiAutocomplete-input": {
			padding: '0px 4px 5px'
		}
	},
	content: {
		overflow: 'hidden',
		flex: 1,
		display: 'flex'
	},
	dates: {
		overflowY: 'auto',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		paddingBottom: 20,
		'&::-webkit-scrollbar': {
			width: 0,
			background: 'transparent'
		},
		'&::-webkit-scrollbar-thumb': {
			background: '#FF0000'
		}
	},
	occurrence: {
		display: 'flex',
		flex: 1
	},
	chip: {
		background: 'rgba(107, 74, 214, 0.1)',
		borderRadius: 6,
		padding: '3px 8px',
		'& > span': {
			fontFamily: 'Nunito',
			fontSize: '14px',
			fontWeight: 600,
			lineHeight: '19px',
			textTransform: 'none',
			color: '#5F38BE',
			marginRight: 0
		}
	}
}))

const EditMedDialog = (props) => {
	const classes = useStyles()
	const theme = useTheme()
	const frequencies = ['Daily', 'As Needed', 'Custom']
	const timeBlocks = ['morning', 'midday', 'evening', 'night']

	let {
		isShown,
		visit,
		med,
		hideEditMedPopup,
		saveMedsToVisit,
		visitMeds,
		setVisitMeds,
		updatedMeds,
		setUpdatedMeds,
		dailyMeds,
		setDailyMeds,
		updatedDailyMeds,
		setUpdatedDailyMeds
	} = props

	const addMed = () => {
		let newMed = {
			med_id: med._id,
			date: null,
			time_blocks: [],
			name: med.name
		}

		setUpdatedMeds(
			[...updatedMeds, newMed].sort(
				(a, b) => moment(a.date).format('YYYYMMDD') - moment(b.date).format('YYYYMMDD')
			)
		)
	}

	const removeMed = (med) => {
		let meds = [...updatedMeds]
		let medIndex = meds.findIndex((m) => m.med_id === med.med_id && m.date === med.date)

		if (medIndex !== -1) {
			meds.splice(medIndex, 1)
			setUpdatedMeds(
				meds.sort((a, b) => moment(a.date).format('YYYYMMDD') - moment(b.date).format('YYYYMMDD'))
			)
		}
	}

	const updateMed = (med, field, value) => {
		let updatedMed = { ...med }
		let meds = [...updatedMeds]
		let medIndex = meds.findIndex((m) => m.med_id === med.med_id && m.date === med.date)

		if (medIndex !== -1) {
			if (field === 'date') {
				updatedMed = { ...updatedMed, date: value }
				updatedMed.time_blocks = (updatedMed.time_blocks || []).filter((t) =>
					isMedTimeBlockApplicable(visit, value, t)
				)
			} else if (field === 'time_blocks') {
				updatedMed = {
					...updatedMed,
					time_blocks: value.filter((t) => isMedTimeBlockApplicable(visit, med.date, t))
				}
			} else {
				updatedMed = { ...updatedMed, [field]: value }
			}

			meds[medIndex] = updatedMed
			setUpdatedMeds(
				meds.sort((a, b) => moment(a.date).format('YYYYMMDD') - moment(b.date).format('YYYYMMDD'))
			)
		}
	}

	const getApplicableTimeBlocks = (date) => {
		if (!date) {
			return []
		}
		return timeBlocks.filter((time) => isMedTimeBlockApplicable(visit, date, time))
	}

	let isDisabled = () => {
		if (med.frequency === 'Daily') {
			return isEqual(dailyMeds, updatedDailyMeds)
		} else {
			return (
				(updatedMeds && updatedMeds.some((m) => !m.date || !m.time_blocks || !m.time_blocks.length)) ||
				isEqual(visitMeds, updatedMeds)
			)
		}
	}

	let updateDailyTime = (value) => {
		let updates = cloneDeep(updatedDailyMeds)

		updates = updates.map((m) => (m.med_id === med._id ? { ...m, times_of_day: value } : m))

		setUpdatedDailyMeds(updates)
	}

	if (!med) return null

	let dailyMedTimes

	if (med.frequency === 'Daily')
		dailyMedTimes =
			updatedDailyMeds && updatedDailyMeds.length
				? updatedDailyMeds.find((m) => med._id === m.med_id)?.times_of_day
				: []

	return (
		<Dialog
			open={isShown}
			className={classes.dialog}
			onClose={() => {
				hideEditMedPopup()
			}}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px',
					minHeight: '325px',
					maxHeight: '555px'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 16
				}}>
				{med ? med.name : 'No Med'}
			</DialogTitle>
			<DialogContent
				classes={{
					root: classes.content
				}}>
				<div
					style={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column'
					}}>
					<div className={classes.fieldWrapper}>
						<TextField
							label="Frequency"
							variant="filled"
							select
							disabled
							value={med ? med.frequency : ''}
							className={clsx(classes.field, 'calculated-field')}>
							{frequencies.map((frequency) => (
								<MenuItem className={classes.frequency} key={frequency} value={frequency}>
									{frequency}
								</MenuItem>
							))}
						</TextField>
					</div>
					{med.frequency !== 'Daily' && (
						<Button
							variant="outlined"
							fullWidth
							className={clsx(classes.headerButton, classes.addMedButton)}
							startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
							disabled={updatedMeds && updatedMeds.length ? updatedMeds.some((m) => !m.date) : false}
							size="large"
							onClick={() => addMed()}>
							Add a date
						</Button>
					)}
					<div className={classes.dates}>
						{med.frequency !== 'Daily' ? (
							updatedMeds
								.filter((m) => m.med_id === med._id)
								.map((m) => (
									<div className={classes.occurrence}>
										<div style={{ marginRight: 27 }}>
											<Icon
												className="fas fa-minus-circle"
												onClick={() => removeMed(m)}
												style={{
													fontSize: '18px',
													color: '#D02C2C',
													marginTop: 19,
													cursor: 'pointer',
													paddingTop: 1
												}}
											/>
										</div>
										<div style={{ flex: 1 }}>
											<div className={classes.fieldWrapper}>
												<TextField
													label="Dates"
													variant="filled"
													select
													value={m && m.date ? m.date : ''}
													className={classes.field}
													onChange={(e) => updateMed(m, 'date', e.target.value)}>
													{visit.dates && visit.dates.length
														? visit.dates.map((date) => (
																<MenuItem
																	disabled={updatedMeds.some(
																		(m) =>
																			m.med_id === med._id && m.date === date
																	)}
																	key={date}
																	value={date}>
																	{date
																		? moment(date).format('MMMM D, YYYY')
																		: ''}
																</MenuItem>
														  ))
														: null}
												</TextField>
											</div>
											<div className={classes.fieldWrapper}>
												<Autocomplete
													multiple
													getOptionLabel={(option) =>
														option
															? option.charAt(0).toUpperCase() + option.slice(1)
															: ''
													}
													disableCloseOnSelect
													size="small"
													id="med-time-block"
													className={classes.autoComplete}
													options={getApplicableTimeBlocks(m.date)}
													value={(m.time_blocks || []).filter((t) =>
														getApplicableTimeBlocks(m.date).includes(t)
													)}
													onChange={(e, v) => updateMed(m, 'time_blocks', v)}
													onClose={(e) => {
														e.target.value = ''
													}}
													renderTags={(selected) =>
														selected.map((value) => (
															<Chip
																className={classes.chip}
																key={uuid()}
																label={
																	value
																		? value.charAt(0).toUpperCase() +
																		  value.slice(1)
																		: ''
																}
															/>
														))
													}
													renderOption={(option, { selected }) => (
														<React.Fragment>
															<Checkbox checked={selected} />
															<ListItemText
																style={{ textTransform: 'capitalize' }}
																primary={option}
															/>
														</React.Fragment>
													)}
													renderInput={(params) => (
														<TextField
															{...params}
															className={classes.field}
															variant="filled"
															fullWidth
															label="Time Block"
														/>
													)}
												/>
											</div>
										</div>
									</div>
								))
						) : (
							<div className={classes.fieldWrapper}>
								<Autocomplete
									multiple
									getOptionLabel={(option) =>
										option ? option.charAt(0).toUpperCase() + option.slice(1) : ''
									}
									disableCloseOnSelect
									size="small"
									id="meds-time-block"
									className={classes.autoComplete}
									options={timeBlocks || []}
									value={dailyMedTimes || []}
									onChange={(e, v) => updateDailyTime(v)}
									onClose={(e) => {
										e.target.value = ''
									}}
									renderTags={(selected) =>
										selected.map((value) => (
											<Chip
												className={classes.chip}
												key={uuid()}
												label={value ? value.charAt(0).toUpperCase() + value.slice(1) : ''}
											/>
										))
									}
									renderOption={(option, { selected }) => (
										<React.Fragment>
											<Checkbox checked={selected} />
											<ListItemText
												style={{ textTransform: 'capitalize' }}
												primary={option}
											/>
										</React.Fragment>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											className={classes.field}
											variant="filled"
											fullWidth
											label="Time Block"
										/>
									)}
								/>
							</div>
						)}
					</div>
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					disabled={isDisabled()}
					onClick={() => {
						saveMedsToVisit()
					}}
					color="secondary">
					Save
				</Button>

				<Button
					variant="outlined"
					onClick={() => {
						setUpdatedMeds([...visitMeds])
						setUpdatedDailyMeds([...dailyMeds])
						hideEditMedPopup(false)
					}}
					color="secondary">
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditMedDialog
